import type { CommandSet } from "./model/command-set";
import { commandSetContains } from "./model/command-set";
import type { Cursor } from "./model/cursor";
import { clampCursor } from "./model/cursor";
import type { EditorState } from "./model/editor-state";
import { pushUndoSnapshot } from "./model/history";
import type { Keystroke } from "./model/keystroke";
import type { Mode } from "./model/mode";
import { EMPTY_PENDING } from "./model/pending-command";
import type { Range, RangeKind } from "./model/range";
import { UNNAMED_REGISTER, withRegister } from "./model/registers";
import { isSingleKeyMotion, MOTIONS } from "./motions";
import { applyChange } from "./operators/change";
import { applyDelete } from "./operators/delete";
import { deleteBlock, extractBlock, toBlockRect } from "./operators/range-edit";
import { applyYank } from "./operators/yank";

/**
 * ビジュアルモード（`v` `V` `Ctrl-v`）のキー入力を処理する。
 *
 * スコープの簡略化: カウント修飾子・`gg` 2打シーケンス・行内検索（f/t）は
 * ビジュアルモード中は対象外とする（時間的制約によるスコープ縮小。単一キーの
 * モーションと d/c/y/x によるオペレータ適用のみ対応）。
 * visual-block（矩形選択）は「行の範囲 × 列の範囲」の矩形として扱い、行をまたいでも
 * その列だけを落とす／写す（`applyBlockOperator`）。矩形の貼り付け（列としての `p`）までは
 * 対応しておらず、写した矩形は行の並びとして貼られる（既知の簡略化）。
 */
export function handleVisualKeystroke(
  state: EditorState,
  keystroke: Keystroke,
  commandSet: CommandSet,
): EditorState {
  const key = keystroke.key;
  const anchor = state.visualAnchor ?? state.cursor;

  if (keystroke.ctrl && key === "v") {
    if (state.mode === "visual-block") {
      return exitVisual(state);
    }
    return { ...state, mode: "visual-block", visualAnchor: anchor };
  }

  if (!keystroke.ctrl && key === "v") {
    if (state.mode === "visual") {
      return exitVisual(state);
    }
    return { ...state, mode: "visual", visualAnchor: anchor };
  }

  if (key === "V") {
    if (state.mode === "visual-line") {
      return exitVisual(state);
    }
    return { ...state, mode: "visual-line", visualAnchor: anchor };
  }

  if (key === "<BS>") {
    const motion = MOTIONS.get("h");
    if (!motion) {
      return state;
    }
    const range = motion(state.buffer, state.cursor, null);
    return { ...state, cursor: clampCursor(state.buffer, range.end), statusMessage: null };
  }

  if (isSingleKeyMotion(key) && commandSetContains(commandSet, key)) {
    const motion = MOTIONS.get(key);
    if (!motion) {
      return state;
    }
    const range = motion(state.buffer, state.cursor, null);
    return { ...state, cursor: clampCursor(state.buffer, range.end), statusMessage: null };
  }

  if (key === "<Del>" || ((key === "d" || key === "x") && commandSetContains(commandSet, "d"))) {
    return state.mode === "visual-block"
      ? applyBlockOperator(state, anchor, "delete", false)
      : applyVisualOperator(state, anchor, applyDelete, false);
  }

  if (key === "c" && commandSetContains(commandSet, "c")) {
    return state.mode === "visual-block"
      ? applyBlockOperator(state, anchor, "delete", true)
      : applyVisualOperator(state, anchor, applyChange, true);
  }

  if (key === "y" && commandSetContains(commandSet, "y")) {
    return state.mode === "visual-block"
      ? applyBlockOperator(state, anchor, "yank", false)
      : applyVisualOperator(state, anchor, applyYank, false, false);
  }

  return { ...state, statusMessage: "そのコマンドはまだ習っていないよ" };
}

/**
 * 矩形選択（`Ctrl-v`）へのオペレータ適用。行の範囲 × 列の範囲だけを落とす／写す。
 * `c` は矩形を落としたうえで左上から挿入を始める（全行への同時挿入までは行わない）。
 */
function applyBlockOperator(
  state: EditorState,
  anchor: Cursor,
  operation: "delete" | "yank",
  entersInsert: boolean,
): EditorState {
  const rect = toBlockRect(anchor, state.cursor);
  const history =
    operation === "delete"
      ? pushUndoSnapshot(state.history, { buffer: state.buffer, cursor: state.cursor })
      : state.history;
  const result =
    operation === "delete"
      ? deleteBlock(state.buffer, rect)
      : { buffer: state.buffer, deletedLines: extractBlock(state.buffer, rect) };
  const registers = withRegister(state.registers, UNNAMED_REGISTER, {
    lines: result.deletedLines,
    linewise: false,
  });
  return {
    ...state,
    buffer: result.buffer,
    cursor: clampCursor(result.buffer, { line: rect.fromLine, col: rect.fromCol }),
    registers,
    history,
    mode: entersInsert ? "insert" : "normal",
    visualAnchor: null,
    pending: EMPTY_PENDING,
    statusMessage: null,
  };
}

function exitVisual(state: EditorState): EditorState {
  return { ...state, mode: "normal", visualAnchor: null, pending: EMPTY_PENDING };
}

function visualRangeKind(mode: Mode): RangeKind {
  return mode === "visual-line" ? "linewise" : "inclusive";
}

type ApplyFn = (
  buffer: EditorState["buffer"],
  range: Range,
) => {
  buffer: EditorState["buffer"];
  cursor: Cursor;
  content: { lines: readonly string[]; linewise: boolean };
};

function applyVisualOperator(
  state: EditorState,
  anchor: Cursor,
  apply: ApplyFn,
  entersInsert: boolean,
  mutates = true,
): EditorState {
  const range: Range = { start: anchor, end: state.cursor, kind: visualRangeKind(state.mode) };
  const history = mutates
    ? pushUndoSnapshot(state.history, { buffer: state.buffer, cursor: state.cursor })
    : state.history;
  const result = apply(state.buffer, range);
  const registers = withRegister(state.registers, UNNAMED_REGISTER, result.content);
  const cursor = clampCursor(result.buffer, result.cursor);
  return {
    ...state,
    buffer: result.buffer,
    cursor,
    registers,
    history,
    mode: entersInsert ? "insert" : "normal",
    visualAnchor: null,
    pending: EMPTY_PENDING,
    statusMessage: null,
  };
}
